import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";

import { useListMemberDependentsQuery } from "@/App/api/member";
import { ContentPanel, DependentDropdown, PageWrapper, ThemedDropdown } from "@/App/components";
import { ToggleButton, ToggleStatus } from "@/App/components/ToggleButton";
import { MemberEligibilityContext } from "@/App/context";
import { useIsDiscountUser, useIsMedicareUser, useIsUnifiedUser } from "@/App/hooks";
import { useAccumulations } from "@/App/hooks/accumulations";
import { Accumulations } from "@/App/pages/PlanContributions/Accumulations/Accumulations";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { DisplayNetworkStatuses, DisplayScopes } from "@/App/types/accumulation";
import { isFamilyCoverage } from "@/App/utils/familyCoverage";

const TRANSLATION_PATH = "PLAN_CONTRIBUTIONS_PAGE";

export const PlanContributionsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { isDiscountUser } = useIsDiscountUser();
  const { isMedicareUser } = useIsMedicareUser();
  const { isUnifiedUser } = useIsUnifiedUser();

  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);

  const [personCode, setPersonCode] = useState<string>(memberEligibility?.member?.person_code);
  const [displayNetworkStatus, setDisplayNetworkStatus] = useState<DisplayNetworkStatuses>(DisplayNetworkStatuses.In);
  const [displayScope, setDisplayScope] = useState<DisplayScopes>(isFamilyCoverage ? DisplayScopes.Family : DisplayScopes.Individual);
  const [showingScopeToggle, setShowingScopeToggle] = useState<boolean>(false);

  const { accumulations, individualAccumulations, familyAccumulations } = useAccumulations(personCode);

  const enableListDependents = displayScope === DisplayScopes.Individual;
  const { data: dependents } = useListMemberDependentsQuery(enableListDependents);

  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);

  const hasDependents = !isViewingDataholder && minorDependents.length > 0;
  const showDependentsDropdown = (enableListDependents && hasDependents);

  useEffect(() => {
    if (personCode === memberEligibility?.member?.person_code && !showingScopeToggle) {
      setShowingScopeToggle(!!(individualAccumulations(accumulations?.results)?.length && familyAccumulations(accumulations?.results)?.length));
    }
  }, [accumulations]);

  const scopes = {
    individual: translate("BENEFITS.commercialBenefits.displayScopeIndividual"),
    family: translate("BENEFITS.commercialBenefits.displayScopeFamily"),
  };

  const onChangeNetworkDisplayType = useCallback((value: ToggleStatus) => {
    if (value === "second") {
      setDisplayNetworkStatus(DisplayNetworkStatuses.Out);
      logEvent(ANALYTIC_EVENTS.MY_RX.outOfNetwork);
    } else {
      setDisplayNetworkStatus(DisplayNetworkStatuses.In);
      logEvent(ANALYTIC_EVENTS.MY_RX.inNetwork);
    }
  }, []);

  const onChangeScopeDisplayType = useCallback((value: string) => {
    if (value === DisplayScopes.Family) {
      setDisplayScope(DisplayScopes.Family);
      logEvent(ANALYTIC_EVENTS.MY_RX.family);
    } else {
      setDisplayScope(DisplayScopes.Individual);
      logEvent(ANALYTIC_EVENTS.MY_RX.individual);
    }
  }, []);

  return (
    <PageWrapper>
      <ContentPanel showBorder title={<Subtitle>{translate(`${TRANSLATION_PATH}.planContributions`)}</Subtitle>}>
        <Stack spacing="large">
          {(showingScopeToggle || showDependentsDropdown) && (
            <Shelf>
              {showingScopeToggle && (
                <ThemedDropdown
                  testId="display-scope-test"
                  onChange={(val: string) => onChangeScopeDisplayType(val as DisplayScopes)}
                  value={displayScope}
                  label={translate("BENEFITS.commercialBenefits.viewFor")}
                >
                  {Object.values(DisplayScopes).map((scope) => (
                    <ThemedDropdown.Option value={scope} key={scope}>
                      {scopes[scope]}
                    </ThemedDropdown.Option>
                  ))}
                </ThemedDropdown>
              )}
              {showDependentsDropdown && (
                <DependentDropdown
                  onChange={(value: string) => setPersonCode(value)}
                  value={personCode}
                  label={translate(`${TRANSLATION_PATH}.individual`)}
                  aria-label={translate(`${TRANSLATION_PATH}.individual`)}
                  dependents={minorDependents}
                  member={memberEligibility.member}
                  showMemberType
                />
              )}
              <Shelf.Spacer />
            </Shelf>
          )}
          {!isDiscountUser && isUnifiedUser && (
            <ToggleButton
              onChange={onChangeNetworkDisplayType}
              firstText={translate("DASHBOARD.dashboardPage.inNetwork")}
              secondText={translate("DASHBOARD.dashboardPage.outOfNetwork")}
            />
          )}
          {!isMedicareUser && !isDiscountUser && (
            <>
              <Accumulations displayNetworkStatus={displayNetworkStatus} displayScope={displayScope} selectedPersonCode={personCode}/>
            </>
          )}
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
